/* eslint-disable max-len */
import { createAction, props } from '@ngrx/store';
import { ControlAccessMaxData, ItemsPagination, MtmaMaxData, SettingsCategoryPaging } from 'src/app/model/api/data-rest';
import { PaginationParams } from 'src/app/model/api/pagination-params';
import { WsEventMessage } from 'src/app/model/api/ws-event-message';
import { BoardUsersInfo } from 'src/app/model/board-users-info';
import { BoardUser, BoardUserDelete } from 'src/app/model/board-user';
import { Command, ConnectedAutomationsCommand } from 'src/app/model/command';

import { ConnectedAutomations, Device } from 'src/app/model/device';
import { DeviceFeature } from 'src/app/model/device-feature';
import { DeviceInfo } from 'src/app/model/device-info';
import { DeviceModel, NewDeviceModel } from 'src/app/model/device-model';
import { DeviceState, DeviceStatus, DeviceStatusUpdate } from 'src/app/model/device-status';
import { GateState } from 'src/app/model/gate-state';
import { IOPayload } from 'src/app/model/io-payload';
import { Manual } from 'src/app/model/manual';
import { ModelState } from 'src/app/model/model-state';
import { RemoteClients } from 'src/app/model/remote-client';
import { SettingsCategory } from 'src/app/model/settings-category';
import { SettingsPayload } from 'src/app/model/settings-payload';
import { SignalData } from 'src/app/model/signal-level';
import { User } from 'src/app/model/user';
import { Profile } from 'src/app/model/profile';
import { SafetyInputCategory } from 'src/app/model/safety-input';
import { InvitationData } from 'src/app/model/api/invitation-data';
import { GateError } from 'src/app/model/gate-error';
import { NotificationLevel } from 'src/app/model/notification';
import { MtmaChangesConfig, MtmaCloneFlag, MtmaModule, MtmaModuleConfig, MtmaSettingsModules } from 'src/app/model/mtma-module';
import { MtmaContact } from 'src/app/model/mtma-contact';
import { MtmaUnit } from 'src/app/model/mtma-unit';
import { MtmaDtmf } from 'src/app/model/mtma-dtmf';
import { MtmaAction } from 'src/app/model/mtma-action';
import { MtmaTag } from 'src/app/model/mtma-tag';
import { MtmaKeypad } from 'src/app/model/mtma-keypad';
import { MtmaTask } from 'src/app/model/mtma-task';
import { DatetimeDeviceData } from 'src/app/model/datetime-device';
import { FirmwaresCategory, FirmwareUpdate, PostFirmwareUpdate } from 'src/app/model/firmware-update';
import { DeviceSpecialDays, DeviceSpecialDaysInfo, DeviceTimers, DeviceTimersInfo, DeviceTimersMeta } from 'src/app/model/device-timer';
import { AccessEvent } from 'src/app/model/access-log';
import { CallUnitEvent } from 'src/app/model/call-access-log';
import { MtmaDiagnosticsLog } from 'src/app/model/mtma-diagnostics-log';
import { MtmaFwAutoUpdateTimeslot, MtmaFwAutoUpdateTimeslotWrite } from 'src/app/model/mtma-fw-autoupdate-timeslot';
import { MtmaRule, MtmaRuleAccess, MtmaTimeslice } from 'src/app/model/mtma-rules-access';
import { CAGroup, CAGroupRequest, CASpecialPlanning, CAWeeklySchedule } from 'src/app/model/control-access';
import { AutomationsAlias, AutomationsText } from 'src/app/model/automations-alias';
import { Permission } from 'src/app/model/permission';
import { MTMACAGroup } from 'src/app/model/mtma-control-access';
import { MtmaExportData } from 'src/app/model/mtma-import-export-file';

export const addDevice = createAction('ADD_DEVICE', props<{ device: Device }>());
export const addDeviceSuccess = createAction('ADD_DEVICE_SUCCESS', props<{ device: Device; message: string }>());
export const addDeviceFailed = createAction('ADD_DEVICE_FAILED', props<{ errorMessage: string, errorDuplicateCrp?: boolean }>());

export const deviceDialogReset = createAction('DEVICE_DIALOG_RESET');
export const editDevice = createAction('EDIT_DEVICE', props<{ device: Device }>());
export const editDeviceSuccess = createAction('EDIT_DEVICE_SUCCESS', props<{ device: Device; message: string }>());
export const editDeviceFailed = createAction('EDIT_DEVICE_FAILED', props<{ errorMessage: string, errorDuplicateCrp?: boolean }>());

export const getCurrentDevice = createAction('GET_CURRENT_DEVICE', props<{ deviceId: number }>());
export const getCurrentDeviceSuccess = createAction('GET_CURRENT_DEVICE_SUCCESS', props<{ device: Device }>());
export const getCurrentDeviceFailed = createAction('GET_CURRENT_DEVICE_FAILED', props<{ errorMessage: string }>());

export const deleteDevice = createAction('DELETE_DEVICE', props<{ deviceId: number }>());
export const deleteDeviceSuccess = createAction('DELETE_DEVICE_SUCCESS', props<{ id: number; message: string }>());
export const deleteDeviceFailed = createAction('DELETE_DEVICE_FAILED', props<{ errorMessage: string }>());

export const deletingSharedDevice = createAction('DELETING_SHARED_DEVICE');
export const deleteSharedDevice = createAction('DELETE_SHARED_DEVICE', props<{ deviceId: number }>());
export const deleteSharedDeviceSuccess = createAction('DELETE_SHARED_DEVICE_SUCCESS', props<{ message: string }>());
export const deleteSharedDeviceFailed = createAction('DELETE_SHARED_DEVICE_FAILED', props<{ errorMessage: string }>());

export const getDevicesStatus = createAction('GET_DEVICES_STATUS', props<{ deviceIds: number[] }>());
export const getDevicesStatusSuccess = createAction('GET_DEVICES_STATUS_SUCCESS', props<{ devicesStatus: DeviceStatus[] }>());
export const getDevicesStatusFailed = createAction('GET_DEVICES_STATUS_FAILED', props<{ errorMessage: string }>());
export const updateCurrentDeviceStatus = createAction('UPDATE_CURRENT_DEVICE_STATUS');
export const updateCurrentDeviceStatusSuccess = createAction('UPDATE_CURRENT_DEVICE_STATUS_SUCCESS', props<{
    deviceStatus: DeviceStatus;
}>()
);
export const updateCurrentDeviceStatusFailed = createAction('UPDATE_CURRENT_DEVICE_STATUS_FAILED', props<{ errorMessage: string }>());
export const getAutomFwStatus = createAction('GET_AUTOM_FW_STATUS');
export const getAutomFwStatusSuccess = createAction(
	'GET_AUTOM_FW_STATUS_SUCCESS',
	props<{ deviceId: number; automFwUpdate: number }>()
);
export const getAutomFwStatusFailed = createAction(
	'GET_AUTOM_FW_STATUS_FAILED',
	props<{ errorMessage: string }>()
);
export const deviceStateUpdate = createAction('DEVICE_STATE_UPDATE', props<{ deviceId: number; event: WsEventMessage }>());
export const deviceStateUpdateSuccess = createAction('DEVICE_STATE_UPDATE_SUCCESS', props<{
    deviceId: number; patch: DeviceStatusUpdate; message: string; notificationLevel?: NotificationLevel;
    applyToCurrentDevice?: boolean;
}>());

export const getDeviceModel = createAction('GET_DEVICE_MODEL', props<{ modelId: number; language?: string; }>());
export const getDeviceModelSuccess = createAction('GET_DEVICE_MODEL_SUCCESS', props<{ deviceModel: DeviceModel }>());
export const getDeviceModelFailed = createAction('GET_DEVICE_MODEL_FAILED', props<{ errorMessage: string }>());

export const getNewDeviceModels = createAction('GET_NEW_DEVICE_MODEL', props<{ categoryId?: number }>());
export const getNewDeviceModelsSuccess = createAction('GET_NEW_DEVICE_MODEL_SUCCESS', props<{ deviceModel: NewDeviceModel[] }>());
export const getNewDeviceModelsFailed = createAction('GET_NEW_DEVICE_MODEL_FAILED', props<{ errorMessage: string }>());


export const getGateStates = createAction('GET_GATE_STATES', props<{ language?: string }>());
export const getGateStatesSuccess = createAction('GET_GATE_STATES_SUCCESS', props<{ gateStates: GateState[] }>());
export const getGateStatesFailed = createAction('GET_GATE_STATES_FAILED', props<{ errorMessage: string }>());

export const getGateErrors = createAction('GET_GATE_ERRORS', props<{ language?: string }>());
export const getGateErrorsSuccess = createAction('GET_GATE_ERRORS_SUCCESS', props<{ gateErrors: GateError[] }>());
export const getGateErrorsFailed = createAction('GET_GATE_ERRORS_FAILED', props<{ errorMessage: string }>());

export const getConfigStates = createAction('GET_CONFIG_STATES', props<{ modelId: number }>());
export const getConfigStatesSuccess = createAction('GET_CONFIG_STATES_SUCCESS', props<{ configStates: ModelState[] }>());
export const getConfigStatesFailed = createAction('GET_CONFIG_STATES_FAILED', props<{ errorMessage: string }>());

export const initDevice = createAction('INIT_DEVICE', props<{ device?: Device; terminals?: IOPayload[] }>());
export const initDeviceSuccess = createAction('INIT_DEVICE_SUCCESS', props<{ id: number; message: string }>());
export const initDeviceFailed = createAction('INIT_DEVICE_FAILED', props<{ errorMessage: string }>());

export const getSignalLevel = createAction('GET_SIGNAL_LEVEL', props<{ deviceId?: number; categoryId?: number }>());
export const getSignalLevelSuccess = createAction('GET_SIGNAL_LEVEL_SUCCESS', props<{ signalData: SignalData }>());
export const getSignalLevelFailed = createAction('GET_SIGNAL_LEVEL_FAILED', props<{ errorMessage: string }>());

export const getCommands = createAction('GET_COMMANDS', props<{ device?: Device }>());
export const getCommandsSuccess = createAction('GET_COMMANDS_SUCCESS', props<{ commands: Command[]; connectedAutomationsCommand?: ConnectedAutomationsCommand[] }>());
export const getCommandsFailed = createAction('GET_COMMANDS_FAILED', props<{ errorMessage: string }>());

export const resetEditCommands = createAction('RESET_EDIT_COMMANDS');
export const editCommands = createAction('EDIT_COMMANDS', props<{ device?: Device; commands: Command[] }>());
export const editCommandsSuccess = createAction('EDIT_COMMANDS_SUCCESS', props<{ commands: Command[]; message: string }>());
export const editCommandsFailed = createAction('EDIT_COMMANDS_FAILED', props<{ errorMessage: string }>());

export const executeCommand = createAction('EXECUTE_COMMAND', props<{ deviceId?: number; modelId?: number; commandId: number }>());
export const executeCommandSuccess = createAction('EXECUTE_COMMAND_SUCCESS', props<{ message: string }>());
export const executeCommandFailed = createAction('EXECUTE_COMMAND_FAILED', props<{ errorMessage: string }>());

export const getFeatures = createAction('GET_FEATURES');
export const getFeaturesSuccess = createAction('GET_FEATURES_SUCCESS', props<{ features: DeviceFeature[] }>());
export const getFeaturesFailed = createAction('GET_FEATURES_FAILED', props<{ errorMessage: string }>());

export const getDeviceInfo = createAction('GET_DEVICE_INFO', props<{ device: Device }>());
export const getDeviceInfoSuccess = createAction('GET_DEVICE_INFO_SUCCESS', props<{ deviceInfo: DeviceInfo }>());
export const getDeviceInfoFailed = createAction('GET_DEVICE_INFO_FAILED', props<{ errorMessage: string }>());

export const getDeviceInfoGatewayByDeviceId = createAction('GET_DEVICE_INFO_GW_BY_DEVICEID', props<{ deviceId: number }>());
export const getDeviceInfoGatewayByDeviceIdSuccess = createAction('GET_DEVICE_INFO__GW_BY_DEVICEID_SUCCESS', props<{ deviceInfo: DeviceInfo }>());
export const getDeviceInfoGatewayByDeviceIdFailed = createAction('GET_DEVICE_INFO__GW_BY_DEVICEID_FAILED', props<{ errorMessage: string }>());

export const getDeviceModels = createAction('GET_DEVICE_MODELS', props<{
    language?: string; pagination?: PaginationParams;
    categoryname?: string; categoryid?: number;
}>());
export const getDeviceModelsSuccess = createAction('GET_DEVICE_MODELS_SUCCESS', props<{ deviceModels: DeviceModel[] }>());
export const getDeviceModelsFailed = createAction('GET_DEVICE_MODELS_FAILED', props<{ errorMessage: string }>());

export const getDeviceModelSettings = createAction('GET_DEVICE_MODEL_SETTINGS', props<{ modelId?: number; language?: string }>());
export const getDeviceModelSettingsSuccess = createAction('GET_DEVICE_MODEL_SETTINGS_SUCCESS', props<{
    SettingsCategories: SettingsCategory[];
}>());
export const getDeviceModelSettingsFailed = createAction('GET_DEVICE_MODEL_SETTINGS_FAILED', props<{ errorMessage: string }>());

export const getDeviceSettings = createAction('GET_DEVICE_SETTINGS', props<{ deviceId?: number }>());
export const getDeviceSettingsSuccess = createAction('GET_DEVICE_SETTINGS_SUCCESS', props<{
    SettingsCategories: SettingsCategory[];
}>());
export const getDeviceSettingsFailed = createAction('GET_DEVICE_SETTINGS_FAILED', props<{ errorMessage: string }>());

export const saveDeviceSettings = createAction('SAVE_DEVICE_SETTINGS', props<{ settings: SettingsPayload }>());
export const saveDeviceSettingsSuccess = createAction('SAVE_DEVICE_SETTINGS_SUCCESS');
export const saveDeviceSettingsFailed = createAction('SAVE_DEVICE_SETTINGS_FAILED', props<{ errorMessage: string }>());

export const getBoardusers = createAction('GET_BOARDUSERS', props<{ paging?: PaginationParams }>());
export const getBoardusersSuccess = createAction('GET_BOARDUSERS_SUCCESS', props<{ paging: ItemsPagination; boardUsers: BoardUser[] }>());
export const getBoardusersFailed = createAction('GET_BOARDUSERS_FAILED', props<{ errorMessage: string }>());

export const getBoardUsersInfo = createAction('GET_BOARDUSERS_INFO', props<{ language?: string }>());
export const getBoardusersInfoSuccess = createAction('GET_BOARDUSERS_INFO_SUCCESS', props<{
    boardUsersInfo: BoardUsersInfo; needResync: boolean; message: string;
}>());
export const getBoardusersInfoFailed = createAction('GET_BOARDUSERS_INFO_FAILED', props<{ errorMessage: string }>());

export const boardUserDialogReset = createAction('BOARD_USER_DIALOG_RESET');
export const addBoardUser = createAction('ADD_BOARD_USER', props<{ boardUser: BoardUser }>());
export const addBoardUserSuccess = createAction('ADD_BOARD_USER_SUCCESS', props<{ paging?: PaginationParams; message: string }>());
export const addBoardUserFailed = createAction('ADD_BOARD_USER_FAILED', props<{ errorMessage: string }>());

export const resetBoarduserAcquire = createAction('RESET_BOARD_USER');
export const acquireBoardUser = createAction('ACQUIRE_BOARD_USER');

export const acquireBoardUserResetDialog = createAction('ACQUIRE_BOARD_USER_RESET_DIALOG');
export const acquireBoardUserSuccess = createAction('ACQUIRE_BOARD_USER_SUCCESS', props<{
    boardUser: BoardUser; message: string; paging?: PaginationParams;
}>());
export const acquireBoardUserFailed = createAction('ACQUIRE_BOARD_USER_FAILED', props<{ errorMessage: string }>());

export const syncBoardUsers = createAction('SYNC_BOARD_USERS');
export const syncBoardUsersSuccess = createAction('SYNC_BOARD_USERS_SUCCESS');
export const syncBoardUsersFailed = createAction('SYNC_BOARD_USERS_FAILED', props<{ errorMessage: string }>());

export const editBoardUser = createAction('EDIT_BOARD_USER', props<{ boardUser: BoardUser; isCodeGroup: boolean }>());
export const editBoardUserSuccess = createAction('EDIT_BOARD_USER_SUCCESS', props<{ message: string }>());
export const editBoardUserFailed = createAction('EDIT_BOARD_USER_FAILED', props<{ errorMessage: string }>());

export const deleteBoardUser = createAction('DELETE_BOARD_USER', props<{ position: number; code: string }>());
export const deleteBoardUserSuccess = createAction('DELETE_BOARD_USER_SUCCESS', props<{ paging?: PaginationParams, message: string }>());
export const deleteBoardUserFailed = createAction('DELETE_BOARD_USER_FAILED', props<{ errorMessage: string }>());

export const addBoardUsers = createAction('ADD_BOARD_USERS', props<{ boardUsers: BoardUser[] }>());
export const addBoardUsersSuccess = createAction('ADD_BOARD_USERS_SUCCESS', props<{ message: string }>());
export const addBoardUsersFailed = createAction('ADD_BOARD_USERS_FAILED', props<{ errorMessage: string }>());

export const deleteAllBoardUsers = createAction('DELETE_ALL_BOARD_USERS');
export const deleteAllBoardUsersSuccess = createAction('DELETE_ALL_BOARD_USERS_SUCCESS', props<{ paging?: PaginationParams, message: string }>());
export const deleteAllBoardUsersFailed = createAction('DELETE_ALL_BOARD_USERS_FAILED', props<{ errorMessage: string }>());


export const deleteBoardUserSelected = createAction('DELETE_BOARD_USER_SELECTED', props<{ boardUsers: BoardUserDelete[] }>());
export const deleteBoardUserSelectedSuccess = createAction('DELETE_BOARD_USER_SELECTED_SUCCESS', props<{ paging?: PaginationParams, message: string }>());
export const deleteBoardUserSelectedFailed = createAction('DELETE_BOARD_USER_SELECTED_FAILED', props<{ errorMessage: string }>());

export const getDeviceModelManuals = createAction('GET_DEVICE_MODEL_MANUALS', props<{ modelId?: number; language?: string }>());
export const getDeviceModelManualsSuccess = createAction('GET_DEVICE_MODEL_MANUALS_SUCCESS', props<{ manuals: Manual[] }>());
export const getDeviceModelManualsFailed = createAction('GET_DEVICE_MODEL_MANUALS_FAILED', props<{ errorMessage: string }>());

export const getFinalUsers = createAction('GET_FINAL_USERS');
export const getFinalUsersSuccess = createAction('GET_FINAL_USERS_SUCCESS', props<{ finalUsers: User[] }>());
export const getFinalUsersFailed = createAction('GET_FINAL_USERS_FAILED', props<{ errorMessage: string }>());

export const changeMaxSlots = createAction('CHANGE_DEVICE_SLOTS', props<{ userId: number; maxRemotes: number }>());
export const changeMaxSlotsSuccess = createAction('CHANGE_DEVICE_SLOTS_SUCCESS', props<{ message: string }>());
export const changeMaxSlotsFailed = createAction('CHANGE_DEVICE_SLOTS_FAILED', props<{ errorMessage: string }>());

export const resetShareDialog = createAction('RESET_SHARE_DIALOG');

// export const shareDevice = createAction('SHARE_DEVICE', props<{
//     email: string; firstName: string; lastName: string; remotesMax: number;
// }>());
// export const shareDeviceSuccess = createAction('SHARE_DEVICE_SUCCESS', props<{ message: string }>());
// export const shareDeviceFailed = createAction('SHARE_DEVICE_FAILED', props<{ errorMessage: string }>());

export const removeShareDevice = createAction('REMOVE_SHARE_DEVICE', props<{ userId: number }>());
export const removeShareDeviceSuccess = createAction('REMOVE_SHARE_DEVICE_SUCCESS', props<{ userId: number; message: string }>());
export const removeShareDeviceFailed = createAction('REMOVE_SHARE_DEVICE_FAILED', props<{ errorMessage: string }>());

export const getRemoteClients = createAction('GET_REMOTE_CLIENTS', props<{ deviceId?: number; userId?: number }>());
export const getRemoteClientsSuccess = createAction('GET_REMOTE_CLIENTS_SUCCESS', props<{ remoteClients: RemoteClients }>());
export const getRemoteClientsFailed = createAction('GET_REMOTE_CLIENTS_FAILED', props<{ errorMessage: string }>());

export const getFinalUserRemoteClients = createAction('GET_FINAL_USER_REMOTE_CLIENTS', props<{ deviceId?: number; userId: number }>());
export const getFinalUserRemoteClientsSuccess = createAction('GET_FINAL_USER_REMOTE_CLIENTS_SUCCESS', props<{
    remoteClients: RemoteClients; userId: number;
}>());
export const getFinalUserRemoteClientsFailed = createAction('GET_FINAL_USER_REMOTE_CLIENTS_FAILED', props<{ errorMessage: string }>());
export const resetFinalUserRemoteClients = createAction('RESET_FINAL_USER_REMOTE_CLIENTS');

export const deleteRemoteClient = createAction('DELETE_REMOTE_CLIENT', props<{ deviceId?: number; slotId: string }>());
export const deleteRemoteClientSuccess = createAction('DELETE_REMOTE_CLIENT_SUCCESS', props<{ slotId: string; message: string }>());
export const deleteRemoteClientFailed = createAction('DELETE_REMOTE_CLIENT_FAILED', props<{ errorMessage: string }>());

export const toggleRemoteClientLock = createAction('TOGGLE_REMOTE_CLIENT_LOCK', props<{
    deviceId?: number; slotId: string; locked: boolean;
}>());
export const toggleRemoteClientLockSuccess = createAction('TOGGLE_REMOTE_CLIENT_LOCK_SUCCESS', props<{
    slotId: string; locked: boolean; message: string;
}>());
export const toggleRemoteClientLockFailed = createAction('TOGGLE_REMOTE_CLIENT_LOCK_FAILED', props<{ errorMessage: string }>());

export const ioDialogReset = createAction('IO_DIALOG_RESET');
export const editMultiIO = createAction('EDIT_MULTI_IO', props<{ device: Device; ioPayload: IOPayload }>());
export const editMultiIOSuccess = createAction('EDIT_MULTI_IO_SUCCESS', props<{ device: Device; message: string }>());
export const editMultiIOFailed = createAction('EDIT_MULTI_IO_FAILED', props<{ errorMessage: string }>());

export const getErrorCodes = createAction('GET_ERROR_CODES');
export const getErrorCodesSuccess = createAction('GET_ERROR_CODES_SUCCESS', props<{ errorCodes: ModelState[] }>());
export const getErrorCodesFailed = createAction('GET_ERROR_CODES_FAILED', props<{ errorMessage: string }>());

export const getProfiles = createAction('GET_PROFILES');
export const getProfilesSuccess = createAction('GET_PROFILES_SUCCESS', props<{ profiles: Profile[] }>());
export const getProfilesFailed = createAction('GET_PROFILES_FAILED', props<{ errorMessage: string }>());

export const getUserProfiles = createAction('GET_USER_PROFILES', props<{ userId: number }>());
export const getUserProfilesSuccess = createAction('GET_USER_PROFILES_SUCCESS', props<{ userId: number; profiles: Profile[] }>());
export const getUserProfilesFailed = createAction('GET_USER_PROFILES_FAILED', props<{ errorMessage: string }>());

export const clearUserProfiles = createAction('CLEAR_USER_PROFILES', props<{ userId: number }>());

export const updateUserProfile = createAction('UPDATE_USER_PROFILE', props<{ userId: number; id: number; checked: boolean }>());
export const updateUserProfileSuccess = createAction('UPDATE_USER_PROFILE_SUCCESS', props<{
    userId: number; id: number; checked: boolean; message: string;
}>());
export const updateUserProfileFailed = createAction('UPDATE_USER_PROFILE_FAILED', props<{ errorMessage: string }>());

export const getSafetyInputs = createAction('GET_SAFETY_INPUTS');
export const getSafetyInputsSuccess = createAction('GET_SAFETY_INPUTS_SUCCESS', props<{ safetyInputs: SafetyInputCategory[] }>());
export const getSafetyInputsFailed = createAction('GET_SAFETY_INPUTS_FAILED', props<{ errorMessage: string }>());

export const confirmShareDevice = createAction('CONFIRM_SHARE_DEVICE', props<{ invitationData: InvitationData }>());
export const confirmShareDeviceSuccess = createAction('CONFIRM_SHARE_DEVICE_SUCCESS', props<{ message: string }>());
export const confirmShareDeviceFailed = createAction('CONFIRM_SHARE_DEVICE_FAILED', props<{ errorMessage: string }>());



export const getMtmaActions = createAction('GET_MTMA_ACTIONS');
export const getMtmaActionsSuccess = createAction('GET_MTMA_ACTIONS_SUCCESS', props<{ actions: MtmaAction[] }>());
export const getMtmaActionsFailed = createAction('GET_MTMA_ACTIONS_FAILED', props<{ errorMessage: string }>());

export const getMtmaAutomationCommands = createAction('GET_MTMA_AUTOMATION_COMMANDS', props<{ deviceId: number }>());
export const getMtmaAutomationCommandsSuccess = createAction('GET_MTMA_AUTOMATION_COMMANDS_SUCCESS', props<{ commands: Command[]; automationId: number }>());
export const getMtmaAutomationCommandsFailed = createAction('GET_MTMA_AUTOMATION_COMMANDS_FAILED', props<{ errorMessage: string }>());

export const getMtmaModules = createAction('GET_MTMA_MODULES');
export const getMtmaModulesSuccess = createAction('GET_MTMA_MODULES_SUCCESS', props<{ modules: MtmaModule[] }>());
export const getMtmaModulesFailed = createAction('GET_MTMA_MODULES_FAILED', props<{ errorMessage: string }>());

export const getMtmaModule = createAction('GET_MTMA_MODULE', props<{ deviceId: number; moduleId: number }>());
export const getMtmaModuleSuccess = createAction('GET_MTMA_MODULE_SUCCESS', props<{ module: MtmaModule }>());
export const getMtmaModuleFailed = createAction('GET_MTMA_MODULE_FAILED', props<{ errorMessage: string }>());

export const addMtmaModule = createAction('ADD_MTMA_MODULE', props<{ module: MtmaModule }>());
export const addMtmaModuleSuccess = createAction('ADD_MTMA_MODULE_SUCCESS', props<{ message: string }>());
export const addMtmaModuleFailed = createAction('ADD_MTMA_MODULE_FAILED', props<{ errorMessage: string }>());

export const resetEditMtmaModule = createAction('RESET_EDIT_MTMA_MODULE');
export const editMtmaModule = createAction('EDIT_MTMA_MODULE', props<{ module: MtmaModule }>());
export const editMtmaModuleSuccess = createAction('EDIT_MTMA_MODULE_SUCCESS', props<{ message: string }>());
export const editMtmaModuleFailed = createAction('EDIT_MTMA_MODULE_FAILED', props<{ errorMessage: string }>());

export const deleteMtmaModule = createAction('DELETE_MTMA_MODULE', props<{ moduleId: number }>());
export const deleteMtmaModuleSuccess = createAction('DELETE_MTMA_MODULE_SUCCESS', props<{ message: string }>());
export const deleteMtmaModuleFailed = createAction('DELETE_MTMA_MODULE_FAILED', props<{ errorMessage: string }>());

export const resetEditParamMtmaModule = createAction('RESET_EDIT_PARAM_MTMA_MODULE');
export const editParamMtmaModule = createAction('EDIT_PARAM_MTMA_MODULE', props<{ module: MtmaModuleConfig, mtmaDeviceId?: number }>());
export const editParamMtmaModuleSuccess = createAction('EDIT_PARAM_MTMA_MODULE_SUCCESS', props<{ message: string }>());
export const editParamMtmaModuleFailed = createAction('EDIT_PARAM_MTMA_MODULE_FAILED', props<{ errorMessage: string }>());

export const resetContactForm = createAction('RESET_CONTACT_FORM');
export const getMtmaContacts = createAction('GET_MTMA_CONTACTS', props<{
    filter?: string;
    sort?: string;
    order?: string;
    size?: number;
    page?: number;
}>());
export const getMtmaContactsSuccess = createAction('GET_MTMA_CONTACTS_SUCCESS',
    props<{ contacts: MtmaContact[]; paging?: ItemsPagination; maxData: MtmaMaxData }>());
export const getMtmaContactsFailed = createAction('GET_MTMA_CONTACTS_FAILED', props<{ errorMessage: string }>());

export const getMtmaContactsAll = createAction('GET_MTMA_CONTACTS_ALL');
export const getMtmaContactsAllSuccess = createAction('GET_MTMA_CONTACTS_ALL_SUCCESS',
    props<{ contacts: MtmaContact[] }>());
export const getMtmaContactsAllFailed = createAction('GET_MTMA_CONTACTS_ALL_FAILED', props<{ errorMessage: string }>());

export const getMtmaContact = createAction('GET_MTMA_CONTACT', props<{ contact: MtmaContact }>());
export const getMtmaContactSuccess = createAction('GET_MTMA_CONTACT_SUCCESS', props<{ contactRequest: MtmaContact; contactResponse: MtmaContact }>());
export const getMtmaContactFailed = createAction('GET_MTMA_CONTACT_FAILED', props<{ errorMessage: string }>());

export const addMtmaContact = createAction('ADD_MTMA_CONTACT', props<{ contact: MtmaContact }>());
export const addMtmaContactSuccess = createAction('ADD_MTMA_CONTACT_SUCCESS', props<{ message: string }>());
export const addMtmaContactFailed = createAction('ADD_MTMA_CONTACT_FAILED', props<{ errorMessage: string }>());

export const editMtmaContact = createAction('EDIT_MTMA_CONTACT', props<{ contact: MtmaContact }>());
export const editMtmaContactSuccess = createAction('EDIT_MTMA_CONTACT_SUCCESS', props<{ message: string }>());
export const editMtmaContactFailed = createAction('EDIT_MTMA_CONTACT_FAILED', props<{ errorMessage: string }>());

export const deleteMtmaContact = createAction('DELETE_MTMA_CONTACT', props<{ contactId: string }>());
export const deleteMtmaContactSuccess = createAction('DELETE_MTMA_CONTACT_SUCCESS', props<{ message: string }>());
export const deleteMtmaContactFailed = createAction('DELETE_MTMA_CONTACT_FAILED', props<{ errorMessage: string }>());

export const postMtmaDiagnosticsState = createAction('POST_MTMA_DIAGNOSTICS_STATE', props<{ deviceId: number }>());
export const postMtmaDiagnosticsStateSuccess = createAction('POST_MTMA_DIAGNOSTICS_STATE_SUCCESS', props<{ message: string }>());
export const postMtmaDiagnosticsStateFailed = createAction('POST_MTMA_DIAGNOSTICS_STATE_FAILED', props<{ errorMessage: string }>());

export const resetUnitForm = createAction('RESET_UNIT_FORM');
export const resetUpdateUnit = createAction('RESET_UPDATE_UNIT');
export const getMtmaUnits = createAction('GET_MTMA_UNITS', props<{
    filter?: string;
    sort?: string;
    order?: string;
    size?: number;
    page?: number;
}>());
export const getMtmaUnitsSuccess = createAction('GET_MTMA_UNITS_SUCCESS',
    props<{ units: MtmaUnit[]; paging?: ItemsPagination; maxData: MtmaMaxData }>());
export const getMtmaUnitsFailed = createAction('GET_MTMA_UNITS_FAILED', props<{ errorMessage: string }>());

export const getMtmaUnit = createAction('GET_MTMA_UNIT_OBJ', props<{ unit: MtmaUnit }>());
export const getMtmaUnitSuccess = createAction('GET_MTMA_UNIT_SUCCESS', props<{ unitRequest: MtmaUnit; unitResponse: MtmaUnit }>());
export const getMtmaUnitFailed = createAction('GET_MTMA_UNIT_FAILED', props<{ errorMessage: string }>());

export const addMtmaUnit = createAction('ADD_MTMA_UNIT', props<{ unit: MtmaUnit }>());
export const addMtmaUnitSuccess = createAction('ADD_MTMA_UNIT_SUCCESS', props<{ message: string }>());
export const addMtmaUnitFailed = createAction('ADD_MTMA_UNIT_FAILED', props<{ errorMessage: string }>());

export const editMtmaUnit = createAction('EDIT_MTMA_UNIT', props<{ unit: MtmaUnit }>());
export const editMtmaUnitSuccess = createAction('EDIT_MTMA_UNIT_SUCCESS', props<{ message: string }>());
export const editMtmaUnitFailed = createAction('EDIT_MTMA_UNIT_FAILED', props<{ errorMessage: string }>());

export const deleteMtmaUnit = createAction('DELETE_MTMA_UNIT', props<{ unitId: string }>());
export const deleteMtmaUnitSuccess = createAction('DELETE_MTMA_UNIT_SUCCESS', props<{ message: string }>());
export const deleteMtmaUnitFailed = createAction('DELETE_MTMA_UNIT_FAILED', props<{ errorMessage: string }>());



export const resetDtmfForm = createAction('RESET_DTMF_FORM');
export const getMtmaDtmfs = createAction('GET_MTMA_DTMFS', props<{
    filter?: string;
    sort?: string;
    order?: string;
    size?: number;
    page?: number;
}>());
export const getMtmaDtmfsSuccess = createAction('GET_MTMA_DTMFS_SUCCESS',
    props<{ dtmfs: MtmaDtmf[]; paging?: ItemsPagination; maxData: MtmaMaxData }>());
export const getMtmaDtmfsFailed = createAction('GET_MTMA_DTMFS_FAILED', props<{ errorMessage: string }>());

export const getMtmaDtmf = createAction('GET_MTMA_DTMF', props<{ deviceId: number; dtmfId: number }>());
export const getMtmaDtmfSuccess = createAction('GET_MTMA_DTMF_SUCCESS', props<{ dtmf: MtmaDtmf }>());
export const getMtmaDtmfFailed = createAction('GET_MTMA_DTMF_FAILED', props<{ errorMessage: string }>());

export const addMtmaDtmf = createAction('ADD_MTMA_DTMF', props<{ dtmf: MtmaDtmf }>());
export const addMtmaDtmfSuccess = createAction('ADD_MTMA_DTMF_SUCCESS', props<{ message: string }>());
export const addMtmaDtmfFailed = createAction('ADD_MTMA_DTMF_FAILED', props<{ errorMessage: string }>());

export const editMtmaDtmf = createAction('EDIT_MTMA_DTMF', props<{ dtmf: MtmaDtmf }>());
export const editMtmaDtmfSuccess = createAction('EDIT_MTMA_DTMF_SUCCESS', props<{ message: string }>());
export const editMtmaDtmfFailed = createAction('EDIT_MTMA_DTMF_FAILED', props<{ errorMessage: string }>());

export const deleteMtmaDtmf = createAction('DELETE_MTMA_DTMF', props<{ dtmfId: string }>());
export const deleteMtmaDtmfSuccess = createAction('DELETE_MTMA_DTMF_SUCCESS', props<{ message: string }>());
export const deleteMtmaDtmfFailed = createAction('DELETE_MTMA_DTMF_FAILED', props<{ errorMessage: string }>());



export const resetTagForm = createAction('RESET_TAG_FORM');
export const getMtmaTags = createAction('GET_MTMA_TAGS', props<{
    filter?: string;
    sort?: string;
    order?: string;
    size?: number;
    page?: number;
}>());
export const getMtmaTagsSuccess = createAction('GET_MTMA_TAGS_SUCCESS',
    props<{ tags: MtmaTag[]; paging?: ItemsPagination; maxData: MtmaMaxData }>());
export const getMtmaTagsFailed = createAction('GET_MTMA_TAGS_FAILED', props<{ errorMessage: string }>());

export const getMtmaTag = createAction('GET_MTMA_TAG', props<{ deviceId: number; tagId: number }>());
export const getMtmaTagSuccess = createAction('GET_MTMA_TAG_SUCCESS', props<{ tag: MtmaTag }>());
export const getMtmaTagFailed = createAction('GET_MTMA_TAG_FAILED', props<{ errorMessage: string }>());

export const addMtmaTag = createAction('ADD_MTMA_TAG', props<{ tag: MtmaTag }>());
export const addMtmaTagSuccess = createAction('ADD_MTMA_TAG_SUCCESS', props<{ message: string }>());
export const addMtmaTagFailed = createAction('ADD_MTMA_TAG_FAILED', props<{ errorMessage: string }>());

export const editMtmaTag = createAction('EDIT_MTMA_TAG', props<{ tag: MtmaTag }>());
export const editMtmaTagSuccess = createAction('EDIT_MTMA_TAG_SUCCESS', props<{ message: string }>());
export const editMtmaTagFailed = createAction('EDIT_MTMA_TAG_FAILED', props<{ errorMessage: string }>());

export const deleteMtmaTag = createAction('DELETE_MTMA_TAG', props<{ tagId: string }>());
export const deleteMtmaTagSuccess = createAction('DELETE_MTMA_TAG_SUCCESS', props<{ message: string }>());
export const deleteMtmaTagFailed = createAction('DELETE_MTMA_TAG_FAILED', props<{ errorMessage: string }>());

export const getMtmaTagsAll = createAction('GET_MTMA_TAGS_ALL');
export const getMtmaTagsAllSuccess = createAction('GET_MTMA_TAGS_ALL_SUCCESS',
    props<{ tags: MtmaTag[] }>());
export const getMtmaTagsAllFailed = createAction('GET_MTMA_TAGS_ALL_FAILED', props<{ errorMessage: string }>());



export const resetKeypadForm = createAction('RESET_KEYPAD_FORM');
export const getMtmaKeypads = createAction('GET_MTMA_KEYPADS', props<{
    filter?: string;
    sort?: string;
    order?: string;
    size?: number;
    page?: number;
}>());
export const getMtmaKeypadsSuccess = createAction('GET_MTMA_KEYPADS_SUCCESS',
    props<{ keypads: MtmaKeypad[]; paging?: ItemsPagination; maxData: MtmaMaxData; invalid: number }>());
export const getMtmaKeypadsFailed = createAction('GET_MTMA_KEYPADS_FAILED', props<{ errorMessage: string }>());

export const getMtmaKeypad = createAction('GET_MTMA_KEYPAD', props<{ deviceId: number; keypadId: number }>());
export const getMtmaKeypadSuccess = createAction('GET_MTMA_KEYPAD_SUCCESS', props<{ keypad: MtmaKeypad }>());
export const getMtmaKeypadFailed = createAction('GET_MTMA_KEYPAD_FAILED', props<{ errorMessage: string }>());

export const addMtmaKeypad = createAction('ADD_MTMA_KEYPAD', props<{ keypad: MtmaKeypad }>());
export const addMtmaKeypadSuccess = createAction('ADD_MTMA_KEYPAD_SUCCESS', props<{ message: string }>());
export const addMtmaKeypadFailed = createAction('ADD_MTMA_KEYPAD_FAILED', props<{ errorMessage: string }>());

export const editMtmaKeypad = createAction('EDIT_MTMA_KEYPAD', props<{ keypad: MtmaKeypad }>());
export const editMtmaKeypadSuccess = createAction('EDIT_MTMA_KEYPAD_SUCCESS', props<{ message: string }>());
export const editMtmaKeypadFailed = createAction('EDIT_MTMA_KEYPAD_FAILED', props<{ errorMessage: string }>());

export const deleteMtmaKeypad = createAction('DELETE_MTMA_KEYPAD', props<{ keypadId: string }>());
export const deleteMtmaKeypadSuccess = createAction('DELETE_MTMA_KEYPAD_SUCCESS', props<{ message: string }>());
export const deleteMtmaKeypadFailed = createAction('DELETE_MTMA_KEYPAD_FAILED', props<{ errorMessage: string }>());

export const getMtmaKeypadsAll = createAction('GET_MTMA_KEYPADS_ALL');
export const getMtmaKeypadsAllSuccess = createAction('GET_MTMA_KEYPADS_ALL_SUCCESS',
    props<{ keypads: MtmaKeypad[] }>());
export const getMtmaKeypadsAllFailed = createAction('GET_MTMA_KEYPADS_ALL_FAILED', props<{ errorMessage: string }>());

export const getMtmaSettingsModules = createAction('GET_MTMA_SETTINGS_MODULES');
export const getMtmaSettingsModulesSuccess = createAction('GET_MTMA_SETTINGS_MODULES_SUCCESS', props<{ listModulesSettings: MtmaSettingsModules[] }>());
export const getMtmaSettingsModulesFailed = createAction('GET_MTMA_SETTINGS_MODULES_FAILED', props<{ errorMessage: string }>());

export const getMtmaChanges = createAction('GET_MTMA_CHANGES');
export const getMtmaChangesSuccess = createAction('GET_MTMA_CHANGES_SUCCESS', props<{ changes: MtmaChangesConfig }>());
export const getMtmaChangesFailed = createAction('GET_MTMA_CHANGES_FAILED', props<{ errorMessage: string }>());

export const getMtmaActiveTask = createAction('GET_MTMA_ACTIVE_TASK');
export const getMtmaActiveTaskSuccess = createAction('GET_MTMA_ACTIVE_TASK_SUCCESS', props<{ task: MtmaTask }>());
export const getMtmaActiveTaskFailed = createAction('GET_MTMA_ACTIVE_TASK_FAILED', props<{ errorMessage: string }>());

export const mtmaWriteConfig = createAction('GET_MTMA_WRITE_CONFIG');
export const mtmaWriteConfigSuccess = createAction('GET_MTMA_WRITE_CONFIG_SUCCESS', props<{ task: MtmaTask }>());
export const mtmaWriteConfigFailed = createAction('GET_MTMA_WRITE_CONFIG_FAILED', props<{ errorMessage: string }>());

export const getMtmaAbort = createAction('GET_MTMA_ABORT_CONFIG', props<{ taskId: string }>());
export const getMtmaAbortSuccess = createAction('GET_MTMA_ABORT_CONFIG_SUCCESS', props<{ task: MtmaTask }>());
export const getMtmaAbortFailed = createAction('GET_MTMA_ABORT_CONFIG_FAILED', props<{ errorMessage: string }>());

// MTMA RULES
export const getMtmaRulesAll = createAction('GET_MTMA_RULES_ALL');
export const getMtmaRulesAllSuccess = createAction('GET_MTMA_RULES_ALL_SUCCESS',
    props<{ rules: MtmaRule[] }>());
export const getMtmaRulesAllFailed = createAction('GET_MTMA_RULES_ALL_FAILED', props<{ errorMessage: string }>());

// MTMA RULES ACCESS
export const resetMtmaRulesAccessForm = createAction('RESET_MTMA_RULES_ACCESS_FORM');
export const getMtmaRulesAccess = createAction('GET_MTMA_RULES_ACCESS', props<{ ruleId: number }>());
export const getMtmaRulesAccessSuccess = createAction('GET_MTMA_RULES_ACCESS_SUCCESS',
    props<{ tags: MtmaTag[]; keypads: MtmaKeypad[]; contacts: MtmaContact[] }>());
export const getMtmaRulesAccessFailed = createAction('GET_MTMA_RULES_ACCESS_FAILED', props<{ errorMessage: string }>());

export const editMtmaRulesAccess = createAction('EDIT_MTMA_RULES_ACCESS', props<{ ruleId: number; ruleAccess: MtmaRuleAccess }>());
export const editMtmaRulesAccessSuccess = createAction('EDIT_MTMA_RULES_ACCESS_SUCCESS', props<{ message: string }>());
export const editMtmaRulesAccessFailed = createAction('EDIT_MTMA_RULES_ACCESS_FAILED', props<{ errorMessage: string }>());

export const resetCloneMtmaForm = createAction('RESET_CLONE_MTMA_FORM');
export const cloneMtma = createAction('CLONE_MTMA', props<{ deviceIdFrom: number; deviceIdTo: number; flags: MtmaCloneFlag; isImpersMode: boolean }>());
export const cloneMtmaSuccess = createAction('CLONE_MTMA_SUCCESS', props<{ message: string }>());
export const cloneMtmaFailed = createAction('CLONE_MTMA_FAILED', props<{ errorMessage: string }>());

export const getDatetimeDevice = createAction('GET_DATETIME_DEVICE');
export const getDatetimeDeviceSuccess = createAction('GET_DATETIME_DEVICE_SUCCESS', props<{ datetimeDevice: DatetimeDeviceData }>());
export const getDatetimeDeviceFailed = createAction('GET_DATETIME_DEVICE_FAILED', props<{ errorMessage: string }>());

//----------------- FIRMWARE MTMA
export const resetFirmwareUpdate = createAction('RESET_FIRMWARE_UPDATE');
export const getFirmwareUpdate = createAction('GET_FIRMWARE_UPDATE', props<{ test: boolean }>());
export const getFirmwareUpdateSuccess = createAction('GET_FIRMWARE_UPDATE_SUCCESS', props<{ listUpdateFirmware: FirmwareUpdate[] }>());
export const getFirmwareUpdateFailed = createAction('GET_FIRMWARE_UPDATE_FAILED', props<{ errorMessage: string }>());

export const postFirmwareUpdate = createAction('POST_FIRMWARE_UPDATE', props<{ firmware: PostFirmwareUpdate; test: boolean }>());
export const postFirmwareUpdateSuccess = createAction('POST_FIRMWARE_UPDATE_SUCCESS', props<{ message: string }>());
export const postFirmwareUpdateFailed = createAction('POST_FIRMWARE_UPDATE_FAILED', props<{ errorMessage: string }>());
//----------------- FIRMWARE MTMA

//----------------- FIRMWARE GW
export const getGWFirmwareUpdate = createAction('GET_GW_FIRMWARE_UPDATE', props<{ test: boolean }>());
export const getGWFirmwareUpdateSuccess = createAction('GET_GW_FIRMWARE_UPDATE_SUCCESS', props<{ updateFirmware: FirmwareUpdate[] }>());
export const getGWFirmwareUpdateFailed = createAction('GET_GW_FIRMWARE_UPDATE_FAILED', props<{ errorMessage: string }>());

export const postGWFirmwareUpdate = createAction('POST_GW_FIRMWARE_UPDATE', props<{ firmware: PostFirmwareUpdate; test: boolean }>());
export const postGWFirmwareUpdateSuccess = createAction('POST_GW_FIRMWARE_UPDATE_SUCCESS', props<{ message: string }>());
export const postGWFirmwareUpdateFailed = createAction('POST_GW_FIRMWARE_UPDATE_FAILED', props<{ errorMessage: string }>());
//----------------- FIRMWARE GW

// RBE ACCESS LOG
export const getRbeAccessLog = createAction('GET_RBE_ACCESS_LOG', props<{
    deviceId: number;
    startDate: Date;
    endDate: Date;
    filter?: string;
    typeAction?: number;
    result?: number;
    output?: string;
    sort?: string;
    order?: string;
    size?: number;
    page?: number;
    actiondevicename?: string;
    commands?: number[];
}>());
export const getRbeAccessLogSuccess = createAction('GET_RBE_ACCESS_LOG_SUCCESS',
    props<{ logs: AccessEvent[]; paging?: ItemsPagination }>());
export const getRbeAccessLogFailed = createAction('GET_RBE_ACCESS_LOG_FAILED', props<{ errorMessage: string }>());


// RBE ACCESS LOG EXPORT
export const resetRbeAccessLogExport = createAction('RESET_RBE_ACCESS_LOG_EXPORT');
export const getRbeAccessLogExport = createAction('GET_RBE_ACCESS_LOG_EXPORT', props<{
    deviceId: number;
    startDate: Date;
    endDate: Date;
    filter?: string;
    typeAction?: number;
    result?: number;
    sort?: string;
    order?: string;
}>());
export const getRbeAccessLogExportSuccess = createAction('GET_RBE_ACCESS_LOG_EXPORT_SUCCESS',
    props<{ logs: AccessEvent[]; paging?: ItemsPagination }>());
export const getRbeAccessLogExportFailed = createAction('GET_RBE_ACCESS_LOG_EXPORT_FAILED', props<{ errorMessage: string }>());

// CALL UNIT LOG
export const getCallUnitEventLog = createAction('GET_CALL_UNIT_LOG', props<{
    deviceId: number;
    startDate: Date;
    endDate: Date;
    unitName?: string;
    sort?: string;
    order?: string;
    size?: number;
    page?: number;
}>());
export const getCallUnitEventLogSuccess = createAction('GET_CALL_UNIT_LOG_SUCCESS',
    props<{ logs: CallUnitEvent[]; paging?: ItemsPagination }>());
export const getCallUnitEventLogFailed = createAction('GET_CALL_UNIT_LOG_FAILED', props<{ errorMessage: string }>());

export const getAutomationDiscover = createAction('GET_AUTOMATION_DISCOVER', props<{ deviceId: number }>());
export const getAutomationDiscoverSuccess = createAction('GET_AUTOMATION_DISCOVER_SUCCESS', props<{ message: string }>());
export const getAutomationDiscoverFailed = createAction('GET_AUTOMATION_DISCOVER_FAILED', props<{ errorMessage: string }>());

// MTMA DIAGNOSTICS LOG
export const getMtmaDiagnosticsLog = createAction('GET_MTMA_DIAGNOSTICS_LOG', props<{
    deviceId: number;
    startDate: Date;
    endDate: Date;
    eventType?: number;
    eventSubType?: number;
    sort?: string;
    order?: string;
    size?: number;
    page?: number;
}>());
export const getMtmaDiagnosticsLogSuccess = createAction('GET_MTMA_DIAGNOSTICS_LOG_SUCCESS',
    props<{ logs: MtmaDiagnosticsLog[]; paging?: ItemsPagination }>());
export const getMtmaDiagnosticsLogFailed = createAction('GET_MTMA_DIAGNOSTICS_LOG_FAILED', props<{ errorMessage: string }>());

export const getMtmaFwAutoUpdateTimeslots = createAction('GET_MTMA_FW_AUTOUPDATE_TIMESLOTS', props<{
    deviceId: number;
    userId: number;
}>());
export const getMtmaFwAutoUpdateTimeslotsSuccess = createAction('GET_MTMA_FW_AUTOUPDATE_TIMESLOTS_SUCCESS',
    props<{ timeslot?: MtmaFwAutoUpdateTimeslot }>());
export const getMtmaFwAutoUpdateTimeslotsFailed = createAction('GET_MTMA_FW_AUTOUPDATE_TIMESLOTS_FAILED', props<{ errorMessage: string }>());

export const postMtmaFwAutoUpdateTimeslots = createAction('POST_MTMA_FW_AUTOUPDATE_TIMESLOTS', props<{
    deviceId: number;
    userId: number;
    body: MtmaFwAutoUpdateTimeslotWrite;
}>());
export const postMtmaFwAutoUpdateTimeslotsSuccess = createAction('POST_MTMA_FW_AUTOUPDATE_TIMESLOTS_SUCCESS', props<{
    message: string;
    timeslot?: MtmaFwAutoUpdateTimeslot;
}>());
export const postMtmaFwAutoUpdateTimeslotsFailed = createAction('POST_MTMA_FW_AUTOUPDATE_TIMESLOTS_FAILED', props<{ errorMessage: string }>());

export const deleteMtmaFwAutoUpdateTimeslots = createAction('DELETE_MTMA_FW_AUTOUPDATE_TIMESLOTS', props<{
    deviceId: number;
    userId: number;
}>());
export const deleteMtmaFwAutoUpdateTimeslotsSuccess = createAction('DELETE_MTMA_FW_AUTOUPDATE_TIMESLOTS_SUCCESS', props<{ message: string }>());
export const deleteMtmaFwAutoUpdateTimeslotsFailed = createAction('DELETE_MTMA_FW_AUTOUPDATE_TIMESLOTS_FAILED', props<{ errorMessage: string }>());

// DEVICE TIMERS
export const getDeviceTimersInfo = createAction('GET_DEVICE_TIMERS_INFO');
export const getDeviceTimersInfoSuccess = createAction('GET_DEVICE_TIMERS_INFO_SUCCESS', props<{
    timersInfo: DeviceTimersInfo;
}>());
export const getDeviceTimersInfoFailed = createAction('GET_DEVICE_TIMERS_INFO_FAILED', props<{ errorMessage: string }>());
export const getDeviceTimers = createAction('GET_DEVICE_TIMERS', props<{
    size?: number;
    from?: number;
}>());
export const getDeviceTimersSuccess = createAction('GET_DEVICE_TIMERS_SUCCESS', props<{
    timers: DeviceTimers[]; meta: DeviceTimersMeta;
}>());
export const getDeviceTimersFailed = createAction('GET_DEVICE_TIMERS_FAILED', props<{ errorMessage: string }>());
export const resetDeviceTimerForm = createAction('RESET_DEVICE_TIMER_FORM');

export const addDeviceTimer = createAction('ADD_DEVICE_TIMER', props<{ timer: DeviceTimers }>());
export const addDeviceTimerSuccess = createAction('ADD_DEVICE_TIMER_SUCCESS', props<{ message: string }>());
export const addDeviceTimerFailed = createAction('ADD_DEVICE_TIMER_FAILED', props<{ errorMessage: string }>());

export const editDeviceTimer = createAction('EDIT_DEVICE_TIMER', props<{ timer: DeviceTimers }>());
export const editDeviceTimerSuccess = createAction('EDIT_DEVICE_TIMER_SUCCESS', props<{ message: string }>());
export const editDeviceTimerFailed = createAction('EDIT_DEVICE_TIMER_FAILED', props<{ errorMessage: string }>());

export const deleteDeviceTimer = createAction('DELETE_DEVICE_TIMER', props<{ position: number }>());
export const deleteDeviceTimerSuccess = createAction('DELETE_DEVICE_TIMER_SUCCESS', props<{ message: string }>());
export const deleteDeviceTimerFailed = createAction('DELETE_DEVICE_TIMER_FAILED', props<{ errorMessage: string }>());

export const deleteDeviceAllTimer = createAction('DELETE_DEVICE_ALL_TIMER');
export const deleteDeviceAllTimerSuccess = createAction('DELETE_DEVICE_ALL_TIMER_SUCCESS', props<{ message: string }>());
export const deleteDeviceAllTimerFailed = createAction('DELETE_DEVICE_ALL_TIMER_FAILED', props<{ errorMessage: string }>());

// DEVICE SPECIAL DAYS
export const getDeviceSpecialDaysInfo = createAction('GET_DEVICE_SPECIAL_DAYS_INFO');
export const getDeviceSpecialDaysInfoSuccess = createAction('GET_DEVICE_SPECIAL_DAYS_INFO_SUCCESS', props<{
    specialDaysInfo: DeviceSpecialDaysInfo;
}>());
export const getDeviceSpecialDaysInfoFailed = createAction('GET_DEVICE_SPECIAL_DAYS_INFO_FAILED', props<{ errorMessage: string }>());
export const getDeviceSpecialDays = createAction('GET_DEVICE_SPECIAL_DAYS', props<{
    size?: number;
    from?: number;
}>());
export const getDeviceSpecialDaysSuccess = createAction('GET_DEVICE_SPECIAL_DAYS_SUCCESS', props<{
    specialDays: DeviceSpecialDays[]; meta: DeviceTimersMeta;
}>());
export const getDeviceSpecialDaysFailed = createAction('GET_DEVICE_SPECIAL_DAYS_FAILED', props<{ errorMessage: string }>());
export const resetDeviceSpecialDayForm = createAction('RESET_DEVICE_SPECIAL_DAY_FORM');

export const addDeviceSpecialDay = createAction('ADD_DEVICE_SPECIAL_DAY', props<{ specialDay: DeviceSpecialDays }>());
export const addDeviceSpecialDaySuccess = createAction('ADD_DEVICE_SPECIAL_DAY_SUCCESS', props<{ message: string }>());
export const addDeviceSpecialDayFailed = createAction('ADD_DEVICE_SPECIAL_DAY_FAILED', props<{ errorMessage: string }>());

export const editDeviceSpecialDay = createAction('EDIT_DEVICE_SPECIAL_DAY', props<{ specialDay: DeviceSpecialDays }>());
export const editDeviceSpecialDaySuccess = createAction('EDIT_DEVICE_SPECIAL_DAY_SUCCESS', props<{ message: string }>());
export const editDeviceSpecialDayFailed = createAction('EDIT_DEVICE_SPECIAL_DAY_FAILED', props<{ errorMessage: string }>());

export const deleteDeviceSpecialDay = createAction('DELETE_DEVICE_SPECIAL_DAY', props<{ position?: number }>());
export const deleteDeviceSpecialDaySuccess = createAction('DELETE_DEVICE_SPECIAL_DAY_SUCCESS', props<{ message: string }>());
export const deleteDeviceSpecialDayFailed = createAction('DELETE_DEVICE_SPECIAL_DAY_FAILED', props<{ errorMessage: string }>());

export const deleteDeviceAllSpecialDay = createAction('DELETE_DEVICE_ALL_SPECIAL_DAY');
export const deleteDeviceAllSpecialDaySuccess = createAction('DELETE_DEVICE_ALL_SPECIAL_DAY_SUCCESS', props<{ message: string }>());
export const deleteDeviceAllSpecialDayFailed = createAction('DELETE_DEVICE_ALL_SPECIAL_DAY_FAILED', props<{ errorMessage: string }>());


export const editNameAutomationConnected = createAction('EDIT_NAME_AUTOMAION_CONNECTED', props<{ automation: ConnectedAutomations }>());
export const editNameAutomationConnectedSuccess = createAction('EDIT_NAME_AUTOMAION_CONNECTED_SUCCESS', props<{ message: string }>());
export const editNameAutomationConnectedFailed = createAction('EDIT_NAME_AUTOMAION_CONNECTED_FAILED', props<{ errorMessage: string }>());


export const getStateDeviceByCommandIdReset = createAction('GET_STATE_DEVICE_BY_COMMAND_ID_RESET');
export const getStateDeviceByCommandId = createAction('GET_STATE_DEVICE_BY_COMMAND_ID', props<{ commandId: number }>());
export const getStateDeviceByCommandIdSuccess = createAction('GET_STATE_DEVICE_BY_COMMAND_ID_SUCCESS', props<{ deviceState: DeviceState }>());
export const getStateDeviceByCommandIdFailed = createAction('GET_STATE_DEVICE_BY_COMMAND_ID_FAILED', props<{ errorMessage: string }>());


export const getNewStateDeviceByCommandIdReset = createAction('GET_NEW_STATE_DEVICE_BY_COMMAND_ID_RESET');
export const getNewStateDeviceByCommandId = createAction('GET_NEW_STATE_DEVICE_BY_COMMAND_ID', props<{ commandId: number }>());
export const getNewStateDeviceByCommandIdSuccess = createAction('GET_NEW_STATE_DEVICE_BY_COMMAND_ID_SUCCESS', props<{ deviceState: DeviceState }>());
export const getNewStateDeviceByCommandIdFailed = createAction('GET_NEW_STATE_DEVICE_BY_COMMAND_ID_FAILED', props<{ errorMessage: string }>());

//---------getNewStateDeviceByCommandId0
export const getNewStateDeviceByCommandId0Reset = createAction('GET_NEW_STATE_DEVICE_BY_COMMAND_ID_0_RESET');
export const getNewStateDeviceByCommandId0 = createAction('GET_NEW_STATE_DEVICE_BY_COMMAND_ID_0');
export const getNewStateDeviceByCommandId0Success = createAction('GET_NEW_STATE_DEVICE_BY_COMMAND_ID_0_SUCCESS', props<{ deviceState: DeviceState }>());
export const getNewStateDeviceByCommandId0Failed = createAction('GET_NEW_STATE_DEVICE_BY_COMMAND_ID_0_FAILED', props<{ errorMessage: string }>());
//---------getNewStateDeviceByCommandId0

export const updateFirmwareVersion = createAction('UPDATE_FIRMWARE_VERSION', props<{ fwVersion: string, message: string }>());

// RBE ACCESS LOG
export const getAccessLog = createAction('GET_ACCESS_LOG', props<{
    deviceId: number;
    startDate: Date;
    endDate: Date;
    filter?: string;
    typeAction?: number;
    result?: number;
    sort?: string;
    order?: string;
    size?: number;
    page?: number;
}>());
export const getAccessLogSuccess = createAction('GET_ACCESS_LOG_SUCCESS',
    props<{ logs: AccessEvent[]; paging?: ItemsPagination }>());
export const getAccessLogFailed = createAction('GET_ACCESS_LOG_FAILED', props<{ errorMessage: string }>());


// RBE ACCESS LOG EXPORT
export const resetAccessLogExport = createAction('RESET_ACCESS_LOG_EXPORT');
export const getAccessLogExport = createAction('GET_ACCESS_LOG_EXPORT', props<{
    deviceId: number;
    startDate: Date;
    endDate: Date;
    filter?: string;
    typeAction?: number;
    result?: number;
    sort?: string;
    order?: string;
}>());
export const getAccessLogExportSuccess = createAction('GET_ACCESS_LOG_EXPORT_SUCCESS',
    props<{ logs: AccessEvent[]; paging?: ItemsPagination }>());
export const getAccessLogExportFailed = createAction('GET_ACCESS_LOG_EXPORT_FAILED', props<{ errorMessage: string }>());

// CALL UNIT LOG EXPORT
export const resetCallUnitLogExport = createAction('RESET_CALL_UNIT_LOG_EXPORT');
export const getCallUnitLogExport = createAction('GET_CALL_UNIT_LOG_EXPORT', props<{
    deviceId: number;
    startDate: Date;
    endDate: Date;
    unitName: string;
    sort?: string;
    order?: string;
}>());
export const getCallUnitLogExportSuccess = createAction('GET_CALL_UNIT_LOG_EXPORT_SUCCESS',
    props<{ logs: CallUnitEvent[]; paging?: ItemsPagination }>());
export const getCallUnitLogExportFailed = createAction('GET_CALL_UNIT_LOG_EXPORT_FAILED', props<{ errorMessage: string }>());

export const passwordVerifySupportReset = createAction('PASSWORD_VERIFY_SUPPORT_RESET');
export const passwordVerifySupportNot = createAction('PASSWORD_VERIFY_SUPPORT_NOT');
export const passwordVerifySupport = createAction('PASSWORD_VERIFY_SUPPORT', props<{ password: string }>());
export const passwordVerifySupportSuccess = createAction('PASSWORD_VERIFY_SUPPORT_SUCCESS', props<{ supported: boolean, enabled: boolean, valid: boolean }>());
export const passwordVerifySupportFailed = createAction('PASSWORD_VERIFY_SUPPORT_FAILED', props<{ errorMessage: string }>());

export const passwordVerify = createAction('PASSWORD_VERIFY', props<{ password: string }>());
export const passwordVerifySuccess = createAction('PASSWORD_VERIFY_SUCCESS', props<{ supported: boolean, enabled: boolean, valid: boolean }>());
export const passwordVerifyInvalid = createAction('PASSWORD_VERIFY_INVALID', props<{ supported: boolean, enabled: boolean, valid: boolean, errorMessage: string }>());
export const passwordVerifyFailed = createAction('PASSWORD_VERIFY_FAILED', props<{ errorMessage: string }>());

export const passwordChangeReset = createAction('PASSWORD_CHANGE_RESET');
export const passwordChange = createAction('PASSWORD_CHANGE', props<{ passwordOld: string, passwordNew: string }>());
export const passwordChangeSuccess = createAction('PASSWORD_CHANGE_SUCCESS', props<{ message: string }>());
export const passwordChangeFailed = createAction('PASSWORD_CHANGE_FAILED', props<{ errorMessage: string }>());




// GROUP
export const resetGroupForm = createAction('RESET_GROUP_FORM');
export const getGroups = createAction('GET_GROUPS', props<{
    filter?: string;
    sort?: string;
    order?: string;
    size?: number;
    page?: number;
}>());
export const getGroupsSuccess = createAction('GET_GROUPS_SUCCESS',
    props<{ groups: CAGroup[]; paging?: ItemsPagination; maxData: ControlAccessMaxData }>());
export const getGroupsFailed = createAction('GET_GROUPS_FAILED', props<{ errorMessage: string }>());

export const getGroup = createAction('GET_GROUP', props<{ deviceId: number; groupId: number }>());
export const getGroupSuccess = createAction('GET_GROUP_SUCCESS', props<{ group: CAGroup }>());
export const getGroupFailed = createAction('GET_GROUP_FAILED', props<{ errorMessage: string }>());

export const addGroup = createAction('ADD_GROUP', props<{ group: CAGroupRequest }>());
export const addGroupSuccess = createAction('ADD_GROUP_SUCCESS', props<{ message: string }>());
export const addGroupFailed = createAction('ADD_GROUP_FAILED', props<{ errorMessage: string }>());

export const editGroup = createAction('EDIT_GROUP', props<{ group: CAGroupRequest }>());
export const editGroupSuccess = createAction('EDIT_GROUP_SUCCESS', props<{ message: string }>());
export const editGroupFailed = createAction('EDIT_GROUP_FAILED', props<{ errorMessage: string }>());

export const deleteGroup = createAction('DELETE_GROUP', props<{ groupId: number }>());
export const deleteGroupSuccess = createAction('DELETE_GROUP_SUCCESS', props<{ message: string }>());
export const deleteGroupFailed = createAction('DELETE_GROUP_FAILED', props<{ errorMessage: string }>());


export const getGroupsAll = createAction('GET_GROUPS_ALL');
export const getGroupsAllSuccess = createAction('GET_GROUPS_ALL_SUCCESS',
    props<{ groups: CAGroup[] }>());
export const getGroupsAllFailed = createAction('GET_GROUPS_ALL_FAILED', props<{ errorMessage: string }>());



// WEEKLY_SCHEDULE
export const resetWeeklyScheduleForm = createAction('RESET_WEEKLY_SCHEDULE_FORM');
export const getWeeklySchedules = createAction('GET_WEEKLY_SCHEDULES', props<{
    likeName?: string;
    sort?: string;
    order?: string;
    size?: number;
    page?: number;
}>());
export const getWeeklySchedulesSuccess = createAction('GET_WEEKLY_SCHEDULES_SUCCESS',
    props<{ weeklySchedules: CAWeeklySchedule[]; paging?: ItemsPagination; maxData: ControlAccessMaxData }>());
export const getWeeklySchedulesFailed = createAction('GET_WEEKLY_SCHEDULES_FAILED', props<{ errorMessage: string }>());

export const getWeeklySchedule = createAction('GET_WEEKLY_SCHEDULE', props<{ deviceId: number; weeklyScheduleId: number }>());
export const getWeeklyScheduleSuccess = createAction('GET_WEEKLY_SCHEDULE_SUCCESS', props<{ weeklySchedule: CAWeeklySchedule }>());
export const getWeeklyScheduleFailed = createAction('GET_WEEKLY_SCHEDULE_FAILED', props<{ errorMessage: string }>());

export const addWeeklySchedule = createAction('ADD_WEEKLY_SCHEDULE', props<{ weeklySchedule: CAWeeklySchedule }>());
export const addWeeklyScheduleSuccess = createAction('ADD_WEEKLY_SCHEDULE_SUCCESS', props<{ message: string }>());
export const addWeeklyScheduleFailed = createAction('ADD_WEEKLY_SCHEDULE_FAILED', props<{ errorMessage: string }>());

export const editWeeklySchedule = createAction('EDIT_WEEKLY_SCHEDULE', props<{ weeklySchedule: CAWeeklySchedule }>());
export const editWeeklyScheduleSuccess = createAction('EDIT_WEEKLY_SCHEDULE_SUCCESS', props<{ message: string }>());
export const editWeeklyScheduleFailed = createAction('EDIT_WEEKLY_SCHEDULE_FAILED', props<{ errorMessage: string }>());

export const deleteWeeklySchedule = createAction('DELETE_WEEKLY_SCHEDULE', props<{ weeklyScheduleId: number }>());
export const deleteWeeklyScheduleSuccess = createAction('DELETE_WEEKLY_SCHEDULE_SUCCESS', props<{ message: string }>());
export const deleteWeeklyScheduleFailed = createAction('DELETE_WEEKLY_SCHEDULE_FAILED', props<{ errorMessage: string }>());

export const getWeeklySchedulesAll = createAction('GET_WEEKLY_SCHEDULES_ALL');
export const getWeeklySchedulesAllSuccess = createAction('GET_WEEKLY_SCHEDULES_ALL_SUCCESS',
    props<{ weeklySchedules: CAWeeklySchedule[] }>());
export const getWeeklySchedulesAllFailed = createAction('GET_WEEKLY_SCHEDULES_ALL_FAILED', props<{ errorMessage: string }>());



// SPECIAL_PLANNING
export const resetSpecialPlanningForm = createAction('RESET_SPECIAL_PLANNING_FORM');
export const getSpecialPlannings = createAction('GET_SPECIAL_PLANNINGS', props<{
    likeName?: string;
    sort?: string;
    order?: string;
    size?: number;
    page?: number;
}>());
export const getSpecialPlanningsSuccess = createAction('GET_SPECIAL_PLANNINGS_SUCCESS',
    props<{ specialPlannings: CASpecialPlanning[]; paging?: ItemsPagination; maxData: ControlAccessMaxData }>());
export const getSpecialPlanningsFailed = createAction('GET_SPECIAL_PLANNINGS_FAILED', props<{ errorMessage: string }>());

export const getSpecialPlanning = createAction('GET_SPECIAL_PLANNING', props<{ deviceId: number; specialPlanningId: number }>());
export const getSpecialPlanningSuccess = createAction('GET_SPECIAL_PLANNING_SUCCESS', props<{ specialPlanning: CASpecialPlanning }>());
export const getSpecialPlanningFailed = createAction('GET_SPECIAL_PLANNING_FAILED', props<{ errorMessage: string }>());

export const addSpecialPlanning = createAction('ADD_SPECIAL_PLANNING', props<{ specialPlanning: CASpecialPlanning }>());
export const addSpecialPlanningSuccess = createAction('ADD_SPECIAL_PLANNING_SUCCESS', props<{ message: string }>());
export const addSpecialPlanningFailed = createAction('ADD_SPECIAL_PLANNING_FAILED', props<{ errorMessage: string }>());

export const editSpecialPlanning = createAction('EDIT_SPECIAL_PLANNING', props<{ specialPlanning: CASpecialPlanning }>());
export const editSpecialPlanningSuccess = createAction('EDIT_SPECIAL_PLANNING_SUCCESS', props<{ message: string }>());
export const editSpecialPlanningFailed = createAction('EDIT_SPECIAL_PLANNING_FAILED', props<{ errorMessage: string }>());

export const deleteSpecialPlanning = createAction('DELETE_SPECIAL_PLANNING', props<{ specialPlanningId: number }>());
export const deleteSpecialPlanningSuccess = createAction('DELETE_SPECIAL_PLANNING_SUCCESS', props<{ message: string }>());
export const deleteSpecialPlanningFailed = createAction('DELETE_SPECIAL_PLANNING_FAILED', props<{ errorMessage: string }>());

export const getSpecialPlanningsAll = createAction('GET_SPECIAL_PLANNINGS_ALL');
export const getSpecialPlanningsAllSuccess = createAction('GET_SPECIAL_PLANNINGS_ALL_SUCCESS',
    props<{ specialPlannings: CASpecialPlanning[] }>());
export const getSpecialPlanningsAllFailed = createAction('GET_SPECIAL_PLANNINGS_ALL_FAILED', props<{ errorMessage: string }>());

// export const getLogThExportReset = createAction('GET_LOG_TH_EXPORT_RESET');
// export const getLogThExport = createAction('GET_LOG_TH_EXPORT', props<{
//     startDate: Date;
//     endDate: Date;
//     metrics?: string[];
// }>());
// export const getLogThExportSuccess = createAction('GET_LOG_TH_EXPORT_SUCCESS',
//     props<{ logThExport: LogThExport }>());
// export const getLogThExportFailed = createAction('GET_LOG_TH_EXPORT_FAILED', props<{ errorMessage: string }>());


//SETTINGS PAGING
export const getDeviceSettingsPagingReset = createAction('GET_DEVICE_SETTINGS_PAGING_RESET');
export const getDeviceSettingsPaging = createAction('GET_DEVICE_SETTINGS_PAGING', props<{ deviceId?: number, page: number }>());
export const getDeviceSettingsPagingSuccess = createAction('GET_DEVICE_SETTINGS_PAGING_SUCCESS', props<{
    SettingsCategoriesPaging: SettingsCategoryPaging;
}>());
export const getDeviceSettingsPagingFailed = createAction('GET_DEVICE_SETTINGS_PAGING_FAILED', props<{ errorMessage: string }>());
//SETTINGS PAGING


//ACINFO SYNC
export const acRbeSync = createAction('AC_RBE_SYNC');
export const acRbeSyncSuccess = createAction('AC_RBE_SYNC_SUCCESS', props<{ message: string }>());
export const acRbeSyncFailed = createAction('AC_RBE_SYNC_FAILED', props<{ errorMessage: string }>());


// ACINFO DELETE
export const acRbeReset = createAction('AC_RBE_RESET');
export const acRbeResetSuccess = createAction('AC_RBE_RESET_SUCCESS', props<{ message: string }>());
export const acRbeResetFailed = createAction('AC_RBE_RESET_FAILED', props<{ errorMessage: string }>());


// TH LOG
// export const getThLog = createAction('GET_TH_LOG', props<{
//     from?: Date,
//     intervaltype?: string,
//     location?: string
// }>());
// export const getThLogSuccess = createAction('GET_TH_LOG_SUCCESS',
//     props<{ thermoLog: ThermoLog; }>());
// export const getThLogFailed = createAction('GET_TH_LOG_FAILED', props<{ errorMessage: string }>());




//  GROUP MTMA
export const resetMtmaGroupForm = createAction('RESET_MTMA_GROUP_FORM');
export const getMtmaGroups = createAction('GET_MTMA_GROUPS', props<{
    filter?: string;
    sort?: string;
    order?: string;
    size?: number;
    page?: number;
}>());
export const getMtmaGroupsSuccess = createAction('GET_MTMA_GROUPS_SUCCESS',
    props<{ groups: MTMACAGroup[]; paging?: ItemsPagination; maxData: MtmaMaxData }>());
export const getMtmaGroupsFailed = createAction('GET_MTMA_GROUPS_FAILED', props<{ errorMessage: string }>());

export const getMtmaGroup = createAction('GET_MTMA_GROUP', props<{ deviceId: number; groupId: number }>());
export const getMtmaGroupSuccess = createAction('GET_MTMA_GROUP_SUCCESS', props<{ group: MtmaRule }>());
export const getMtmaGroupFailed = createAction('GET_MTMA_GROUP_FAILED', props<{ errorMessage: string }>());

export const addMtmaGroup = createAction('ADD_MTMA_GROUP', props<{ group: MtmaRule }>());
export const addMtmaGroupSuccess = createAction('ADD_MTMA_GROUP_SUCCESS', props<{ message: string }>());
export const addMtmaGroupFailed = createAction('ADD_MTMA_GROUP_FAILED', props<{ errorMessage: string }>());

export const editMtmaGroup = createAction('EDIT_MTMA_GROUP', props<{ group: MtmaRule }>());
export const editMtmaGroupSuccess = createAction('EDIT_MTMA_GROUP_SUCCESS', props<{ message: string }>());
export const editMtmaGroupFailed = createAction('EDIT_MTMA_GROUP_FAILED', props<{ errorMessage: string }>());

export const deleteMtmaGroup = createAction('DELETE_MTMA_GROUP', props<{ groupId: number }>());
export const deleteMtmaGroupSuccess = createAction('DELETE_MTMA_GROUP_SUCCESS', props<{ message: string }>());
export const deleteMtmaGroupFailed = createAction('DELETE_MTMA_GROUP_FAILED', props<{ errorMessage: string }>());


export const getMtmaGroupsAll = createAction('GET_MTMA_GROUPS_ALL');
export const getMtmaGroupsAllSuccess = createAction('GET_MTMA_GROUPS_ALL_SUCCESS',
    props<{ mtmaGroups: MTMACAGroup[] }>());
export const getMtmaGroupsAllFailed = createAction('GET_MTMA_GROUPS_ALL_FAILED', props<{ errorMessage: string }>());


// WEEKLY_SCHEDULE
export const resetMtmaWeeklyScheduleForm = createAction('RESET_MTMA_WEEKLY_SCHEDULE_FORM');
export const getMtmaWeeklySchedules = createAction('GET_MTMA_WEEKLY_SCHEDULES', props<{
    likeName?: string;
    sort?: string;
    order?: string;
    size?: number;
    page?: number;
}>());
export const getMtmaWeeklySchedulesSuccess = createAction('GET_MTMA_WEEKLY_SCHEDULES_SUCCESS',
    props<{ weeklySchedules: MtmaTimeslice[]; paging?: ItemsPagination; maxData: MtmaMaxData }>());
export const getMtmaWeeklySchedulesFailed = createAction('GET_MTMA_WEEKLY_SCHEDULES_FAILED', props<{ errorMessage: string }>());

export const getMtmaWeeklySchedule = createAction('GET_MTMA_WEEKLY_SCHEDULE', props<{ deviceId: number; weeklyScheduleId: number }>());
export const getMtmaWeeklyScheduleSuccess = createAction('GET_MTMA_WEEKLY_SCHEDULE_SUCCESS', props<{ weeklySchedule: MtmaTimeslice }>());
export const getMtmaWeeklyScheduleFailed = createAction('GET_MTMA_WEEKLY_SCHEDULE_FAILED', props<{ errorMessage: string }>());

export const addMtmaWeeklySchedule = createAction('ADD_MTMA_WEEKLY_SCHEDULE', props<{ weeklySchedule: MtmaTimeslice }>());
export const addMtmaWeeklyScheduleSuccess = createAction('ADD_MTMA_WEEKLY_SCHEDULE_SUCCESS', props<{ message: string }>());
export const addMtmaWeeklyScheduleFailed = createAction('ADD_MTMA_WEEKLY_SCHEDULE_FAILED', props<{ errorMessage: string }>());

export const editMtmaWeeklySchedule = createAction('EDIT_MTMA_WEEKLY_SCHEDULE', props<{ weeklySchedule: MtmaTimeslice }>());
export const editMtmaWeeklyScheduleSuccess = createAction('EDIT_MTMA_WEEKLY_SCHEDULE_SUCCESS', props<{ message: string }>());
export const editMtmaWeeklyScheduleFailed = createAction('EDIT_MTMA_WEEKLY_SCHEDULE_FAILED', props<{ errorMessage: string }>());

export const deleteMtmaWeeklySchedule = createAction('DELETE_MTMA_WEEKLY_SCHEDULE', props<{ weeklyScheduleId: number }>());
export const deleteMtmaWeeklyScheduleSuccess = createAction('DELETE_MTMA_WEEKLY_SCHEDULE_SUCCESS', props<{ message: string }>());
export const deleteMtmaWeeklyScheduleFailed = createAction('DELETE_MTMA_WEEKLY_SCHEDULE_FAILED', props<{ errorMessage: string }>());

export const getMtmaWeeklySchedulesAll = createAction('GET_MTMA_WEEKLY_SCHEDULES_ALL');
export const getMtmaWeeklySchedulesAllSuccess = createAction('GET_MTMA_WEEKLY_SCHEDULES_ALL_SUCCESS',
    props<{ weeklySchedules: MtmaTimeslice[] }>());
export const getMtmaWeeklySchedulesAllFailed = createAction('GET_MTMA_WEEKLY_SCHEDULES_ALL_FAILED', props<{ errorMessage: string }>());



// SPECIAL_PLANNING
export const resetMtmaSpecialPlanningForm = createAction('RESET_MTMA_SPECIAL_PLANNING_FORM');
export const getMtmaSpecialPlannings = createAction('GET_MTMA_SPECIAL_PLANNINGS', props<{
    likeName?: string;
    sort?: string;
    order?: string;
    size?: number;
    page?: number;
}>());
export const getMtmaSpecialPlanningsSuccess = createAction('GET_MTMA_SPECIAL_PLANNINGS_SUCCESS',
    props<{ specialPlannings: MtmaTimeslice[]; paging?: ItemsPagination; maxData: MtmaMaxData }>());
export const getMtmaSpecialPlanningsFailed = createAction('GET_MTMA_SPECIAL_PLANNINGS_FAILED', props<{ errorMessage: string }>());

export const getMtmaSpecialPlanning = createAction('GET_MTMA_SPECIAL_PLANNING', props<{ deviceId: number; specialPlanningId: number }>());
export const getMtmaSpecialPlanningSuccess = createAction('GET_MTMA_SPECIAL_PLANNING_SUCCESS', props<{ specialPlanning: MtmaTimeslice }>());
export const getMtmaSpecialPlanningFailed = createAction('GET_MTMA_SPECIAL_PLANNING_FAILED', props<{ errorMessage: string }>());

export const addMtmaSpecialPlanning = createAction('ADD_MTMA_SPECIAL_PLANNING', props<{ specialPlanning: MtmaTimeslice }>());
export const addMtmaSpecialPlanningSuccess = createAction('ADD_MTMA_SPECIAL_PLANNING_SUCCESS', props<{ message: string }>());
export const addMtmaSpecialPlanningFailed = createAction('ADD_MTMA_SPECIAL_PLANNING_FAILED', props<{ errorMessage: string }>());

export const editMtmaSpecialPlanning = createAction('EDIT_MTMA_SPECIAL_PLANNING', props<{ specialPlanning: MtmaTimeslice }>());
export const editMtmaSpecialPlanningSuccess = createAction('EDIT_MTMA_SPECIAL_PLANNING_SUCCESS', props<{ message: string }>());
export const editMtmaSpecialPlanningFailed = createAction('EDIT_MTMA_SPECIAL_PLANNING_FAILED', props<{ errorMessage: string }>());

export const deleteMtmaSpecialPlanning = createAction('DELETE_MTMA_SPECIAL_PLANNING', props<{ specialPlanningId: number }>());
export const deleteMtmaSpecialPlanningSuccess = createAction('DELETE_MTMA_SPECIAL_PLANNING_SUCCESS', props<{ message: string }>());
export const deleteMtmaSpecialPlanningFailed = createAction('DELETE_MTMA_SPECIAL_PLANNING_FAILED', props<{ errorMessage: string }>());

export const getMtmaSpecialPlanningsAll = createAction('GET_MTMA_SPECIAL_PLANNINGS_ALL');
export const getMtmaSpecialPlanningsAllSuccess = createAction('GET_MTMA_SPECIAL_PLANNINGS_ALL_SUCCESS',
    props<{ specialPlannings: MtmaTimeslice[] }>());
export const getMtmaSpecialPlanningsAllFailed = createAction('GET_MTMA_SPECIAL_PLANNINGS_ALL_FAILED', props<{ errorMessage: string }>());


//---------------UPDATE DEVICE RTC

export const updateDeviceAutomationRtc = createAction('DEVICE_UPDATE_AUTOMATIONS_RTC');
export const updateDeviceAutomationRtcSuccess = createAction('DEVICE_UPDATE_AUTOMATIONS_RTC_SUCCESS', props<{ message: string }>());
export const updateDeviceAutomationRtcFailed = createAction('DEVICE_UPDATE_AUTOMATIONS_RTC_FAILED', props<{ errorMessage: string }>());


//---------------UPDATE DEVICE RTC

export const getAutomationsFirmwares = createAction('GET_AUTOMATIONS_FIRMWARES', props<{ test: boolean }>());
export const getAutomationsFirmwaresSuccess = createAction('GET_AUTOMATIONS_FIRMWARES_SUCCESS', props<{ automationsFirmwaresList: FirmwaresCategory }>());
export const getAutomationsFirmwaresFailed = createAction('GET_AUTOMATIONS_FIRMWARES_FAILED', props<{ errorMessage: string }>());



//---------------AUTOMATIONS ALIAS
export const getAutomationsAlias = createAction('GET_AUTOMATIONS_ALIAS');
export const getAutomationsAliasSuccess = createAction('GET_AUTOMATIONS_ALIAS_SUCCESS', props<{ automationsAlias: AutomationsAlias; }>());
export const getAutomationsAliasFailed = createAction('GET_AUTOMATIONS_ALIAS_FAILED', props<{ errorMessage: string }>());

export const resetEditAutomationsAlias = createAction('RESET_AUTOMATIONS_ALIAS_FORM');
export const editAutomationsAlias = createAction('EDIT_AUTOMATIONS_ALIAS', props<{ automationsAlias: AutomationsAlias }>());
export const editAutomationsAliasSuccess = createAction('EDIT_AUTOMATIONS_ALIAS_SUCCESS', props<{ message: string }>());
export const editAutomationsAliasFailed = createAction('EDIT_AUTOMATIONS_ALIAS_FAILED', props<{ errorMessage: string }>());
//---------------AUTOMATIONS ALIAS

//---------------AUTOMATIONS TEXT
export const getAutomationsText = createAction('GET_AUTOMATIONS_TEXT');
export const getAutomationsTextSuccess = createAction('GET_AUTOMATIONS_TEXT_SUCCESS', props<{ automationsText: AutomationsText }>());
export const getAutomationsTextFailed = createAction('GET_AUTOMATIONS_TEXT_FAILED', props<{ errorMessage: string }>());
//---------------AUTOMATIONS TEXT

//---------- MTMA PERMISSIONS
export const getMtmaPermissions = createAction('GET_MTMA_PERMISSIONS', props<{
    filter?: string;
    sort?: string;
    order?: string;
    size?: number;
    page?: number;
}>());
export const getMtmaPermissionsSuccess = createAction('GET_MTMA_PERMISSIONS_SUCCESS',
    props<{ permissions: Permission[]; paging?: ItemsPagination; maxData: MtmaMaxData }>());
export const getMtmaPermissionsFailed = createAction('GET_MTMA_PERMISSIONS_FAILED', props<{ errorMessage: string }>());
//---------- MTMA PERMISSIONS

//---------- MTMA EXPORT LIST CSV
export const getMtmaExportListCsv = createAction('GET_MTMA_EXPORT_LIST_CSV', props<{ exportListId: number; userId?: number }>());
export const getMtmaExportListCsvSuccess = createAction('GET_MTMA_EXPORT_LIST_CSV_SUCCESS',
    props<{ mtmaExportData: MtmaExportData[] }>());
export const getMtmaExportListCsvFailed = createAction('GET_MTMA_EXPORT_LIST_CSV_FAILED', props<{ errorMessage: string }>());
//---------- MTMA EXPORT LIST CSV
